/**
 * Blog data comes from static JSON written at build time by
 * scripts/fetch-blog-data.js (public/blog-data/...), not from Firestore.
 *
 * index.js awaits preloadBlogDataForPath() BEFORE the first render, so on a
 * direct page load the blog components can read their data synchronously
 * from this cache. That makes the first client render identical to the
 * react-snap prerendered HTML (no hydration mismatch, no loading spinner,
 * nothing for Google's renderer to wait on). Client-side navigation between
 * pages just loads the JSON on demand.
 */
export const BLOG_LANGS = ['en', 'es', 'hy', 'ru'];
const SAFE_SLUG = /^[a-z0-9-]+$/;

const cache = { index: undefined, posts: {} };

export const getCachedIndex = () => cache.index;
// undefined = not loaded yet, null = no such post
export const getCachedPost = (slug) => cache.posts[slug];

export async function loadBlogIndex() {
  if (cache.index) return cache.index;
  const res = await fetch('/blog-data/index.json');
  if (!res.ok) throw new Error(`blog index ${res.status}`);
  cache.index = await res.json();
  return cache.index;
}

export async function loadBlogPost(slug) {
  if (cache.posts[slug] !== undefined) return cache.posts[slug];
  if (!SAFE_SLUG.test(slug || '')) {
    cache.posts[slug] = null;
    return null;
  }
  const res = await fetch(`/blog-data/posts/${slug}.json`);
  if (res.status === 404) {
    cache.posts[slug] = null;
    return null;
  }
  if (!res.ok) throw new Error(`blog post ${res.status}`);
  // nginx answers a missing file with the HTML 404 page; treat non-JSON as missing.
  const type = res.headers.get('content-type') || '';
  if (!type.includes('json')) {
    cache.posts[slug] = null;
    return null;
  }
  cache.posts[slug] = await res.json();
  return cache.posts[slug];
}

export async function preloadBlogDataForPath(pathname) {
  const m = String(pathname).match(/^\/(en|es|hy|ru)\/blog(?:\/([^/]+))?\/?$/);
  if (!m) return;
  try {
    if (m[2]) await loadBlogPost(decodeURIComponent(m[2]));
    else await loadBlogIndex();
  } catch (err) {
    // Components fall back to loading on their own; never block the render.
  }
}

export const pickLang = (map, lang) => (map && (map[lang] || map.en)) || '';

export const blogPostPath = (lang, slug) => `/${lang}/blog/${slug}/`;

// "2026-04-06" -> "04/06/2026": fixed format so the prerendered HTML and the
// visitor's browser always produce the exact same text (Intl date formatting
// can differ between browsers, which would break hydration).
export const formatPostDate = (iso) => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : '';
};

// Blank line = new paragraph; single line breaks are kept inside a paragraph
// (rendered with white-space: pre-line). Text with no blank lines at all is
// split on every line break instead, so it never renders as one giant block.
export const toParagraphs = (text) => {
  const str = String(text || '');
  const separator = /\n\s*\n/.test(str) ? /\n\s*\n/ : /\n/;
  return str
    .split(separator)
    .map((p) => p.replace(/^\s+|\s+$/g, ''))
    .filter(Boolean);
};
